import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionArticle.module.css';

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show article content.
 * The article content is mainly supposed to be inside a block.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'article'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionArticle = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  /**
   * Rechtsseiten (AGB/Datenschutz) bekommen ein eigenes Layout statt der
   * Landingpage-Bänder — die Section-IDs sind in
   * `src/assets/hosted-page-defaults/*PageAsset.js` vergeben:
   *   *-plain      Kurzfassung als ruhige Editorial-Karte
   *   privacy-toc  Inhaltsverzeichnis (Sprungmarken sind die Section-IDs)
   *   privacy-doc-* nummerierte Abschnitte der verbindlichen Fassung
   */
  const isLegalPlainSummary = sectionId === 'tos-plain' || sectionId === 'privacy-plain';
  const isLegalToc = sectionId === 'privacy-toc';
  const isLegalDoc =
    sectionId === 'tos-legal' ||
    (typeof sectionId === 'string' && sectionId.startsWith('privacy-doc-'));
  const isLegalCard = isLegalPlainSummary || isLegalToc || isLegalDoc;

  const headerMaybe = hasHeaderFields ? (
    <header className={defaultClasses.sectionDetails}>
      <Field data={title} className={defaultClasses.title} options={fieldOptions} />
      <Field data={description} className={defaultClasses.description} options={fieldOptions} />
      <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
    </header>
  ) : null;

  const blocksMaybe = hasBlocks ? (
    <div
      className={classNames(defaultClasses.blockContainer, css.articleMain, {
        [css.noSidePaddings]: isInsideContainer,
        [css.legalPlainArticleMain]: isLegalCard,
      })}
    >
      <BlockBuilder
        blocks={blocks}
        sectionId={sectionId}
        ctaButtonClass={defaultClasses.ctaButton}
        options={options}
      />
    </div>
  ) : null;

  const body = isLegalCard ? (
    <div
      className={classNames(css.legalCardWrap, {
        [css.legalPlainSummaryWrap]: isLegalPlainSummary,
        [css.legalTocWrap]: isLegalToc,
        [css.legalDocWrap]: isLegalDoc,
      })}
    >
      {headerMaybe}
      {blocksMaybe}
    </div>
  ) : (
    <>
      {headerMaybe}
      {blocksMaybe}
    </>
  );

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(className, {
        [css.legalCardSection]: isLegalCard,
        [css.legalPlainSummarySection]: isLegalPlainSummary,
        [css.legalTocSection]: isLegalToc,
        [css.legalDocSection]: isLegalDoc,
      })}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {body}
    </SectionContainer>
  );
};

export default SectionArticle;
